import { useContext, useEffect } from 'react';

import * as api from '../../../../../api';

import type { ChatConversation } from '../../../../../common';
import type { ReschedulingIntentMessage } from '../../../../../common/rescheduling';
import Reconciliation from '../GeneralUi/Reconciliation';
import { ConfigurationContext } from '../../../../../context/configurationContext';
import { isEnumMember } from 'common/dist/utils/types';
import Rescheduling from './Rescheduling';
import ViewOrPayBill from './Payment';
import BaseMessage from '../Base';
import { ConversationContext } from '../../../../../context/conversationContext';
import ProviderPanel from './Panel/Provider';
import { getProviderCards } from '../../../../../utils/provider';
import { isLastMessage } from '../../../../../utils/date';

const INTENT_REQUIRING_AUTH: { [key in api.Assistant.Intent]?: boolean } = {
  [api.Assistant.Intent.Payment]: true,
  [api.Assistant.Intent.Rescheduling]: true,
} as const;

export const INTENT_WITH_PANEL: { [key in api.Assistant.Intent]?: boolean } = {
  [api.Assistant.Intent.ProviderBrowse]: true,
} as const;

export const SUPPORTED_INTENTS: {
  [key in api.Assistant.Intent]?: boolean;
} = {
  [api.Assistant.Intent.ProviderSearch]: true,
  [api.Assistant.Intent.Payment]: true,
  [api.Assistant.Intent.Rescheduling]: true,
} as const;

interface Props {
  message: ChatConversation;
  showTextAnimation: boolean;
}

const Intent: React.FC<Props> = ({ message, showTextAnimation }) => {
  const configurationContext = useContext(ConfigurationContext);
  const conversationContext = useContext(ConversationContext);

  useEffect(() => {
    if (!('intent' in message)) return;
    if (!conversationContext?.messages) return;
    if (!isLastMessage(message, conversationContext.messages)) return;
    if (!isEnumMember(api.Assistant.Intent, message.intent)) return;
    const { providers } = getProviderCards(message);
    if (providers === undefined) return;
    if (!INTENT_WITH_PANEL[message.intent]) return;
    conversationContext?.setPanelContent(<ProviderPanel message={message} />);
    // eslint-disable-next-line
  }, [message]);

  if (!('intent' in message)) {
    return <></>;
  }

  if (
    !configurationContext?.authenticated &&
    isEnumMember(api.Assistant.Intent, message.intent) &&
    INTENT_REQUIRING_AUTH[message.intent] === true
  ) {
    return (
      <Reconciliation
        message={message}
        showTextAnimation={showTextAnimation}
        onReconciliationSuccess={() => {}}
      />
    );
  }

  switch (message.intent) {
    case api.Assistant.Intent.Payment:
      return (
        <ViewOrPayBill
          message={message}
          showTextAnimation={showTextAnimation}
        />
      );
    case api.Assistant.Intent.Rescheduling:
      return (
        <Rescheduling
          message={message as ReschedulingIntentMessage}
          showTextAnimation={showTextAnimation}
        />
      );
    default:
      return (
        <BaseMessage message={message} showTextAnimation={showTextAnimation} />
      );
  }
};

export default Intent;
